import { useState, useEffect } from "react";
import { useGlobalContext } from "../../GlobalContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import DriversReportsTab from "./DriversReportsTab";
import PackkyReportsTab from "./PackkyReportsTab";
import DriverIntelligenceTab from "./DriverIntelligenceTab";
import DriverPointsTab from "./DriverPointsTab";

import { exportAnyJsonToExcel } from "../Utils/exportAnyJsonToExcel";
import Logo from "../../Images/Packky.svg";
import LoadingScreen from "../Utils/LoadingScreen";
import TablePagination from "../Utils/TablePagination";

import useReports from "../../Sevices/useReports";
import useDriver from "../../Sevices/Driver Api/useDriver";

const ReportsDashboard = () => {
  const { accessToken, admin } = useGlobalContext();
  const navigate = useNavigate();
const { fetchPackkyData, loading, loadingDrivers } = useReports();

  const { drivers, fetchDrivers } = useDriver();

  const today = new Date().toISOString().split("T")[0];

  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);

  const [restaurantData, setRestaurantData] = useState([]);
  const [driverTaskAnalytics, setDriverTaskAnalytics] = useState([]);
  const [taskStatusAnalytics, setTaskStatusAnalytics] = useState([]);
  const [activeTab, setActiveTab] = useState("packky");
  const [search, setSearch] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);

  const renderPagination = () => {
    const totalCount = filteredDrivers?.length || 0;

    if (totalCount <= driversPerPage) return null;

    return (
      <TablePagination
        currentPage={currentPage}
        totalCount={totalCount}
        pageSize={driversPerPage}
        onPageChange={setCurrentPage}
      />
    );
  };

  const handlePackkyExport = () => {
    if (
      taskStatusAnalytics.length === 0 &&
      restaurantData.length === 0 &&
      driverTaskAnalytics.length === 0
    ) {
      toast.error("No Packky report data available to export");
      return;
    }

    exportAnyJsonToExcel({
      data: taskStatusAnalytics,
      filePrefix: `Packky_Task_Status_${fromDate}_to_${toDate}`,
      sheetName: "Task Status",
    });

    exportAnyJsonToExcel({
      data: restaurantData,
      filePrefix: `Packky_Restaurant_Tasks_${fromDate}_to_${toDate}`,
      sheetName: "Restaurant Tasks",
    });

    exportAnyJsonToExcel({
      data: driverTaskAnalytics,
      filePrefix: `Packky_Driver_Tasks_${fromDate}_to_${toDate}`,
      sheetName: "Driver Tasks",
    });
  };

  const filteredDrivers = drivers?.filter((driver) => {
    if (!search) return true;

    const q = search.toLowerCase();

    return (
      driver.fullName?.toLowerCase().includes(q) ||
      driver.first_name?.toLowerCase().includes(q) ||
      driver.last_name?.toLowerCase().includes(q) ||
      driver.username?.toLowerCase().includes(q) ||
      driver.driverId?.toLowerCase().includes(q) ||
      driver.email?.toLowerCase().includes(q) ||
      driver.phone?.toLowerCase().includes(q) ||
      driver.teamName?.toLowerCase().includes(q)
    );
  });

  const driversPerPage = 15;
  const [currentPage, setCurrentPage] = useState(1);
  const indexOfLast = currentPage * driversPerPage;
  const indexOfFirst = indexOfLast - driversPerPage;
  const currentDrivers = filteredDrivers?.slice(indexOfFirst, indexOfLast);

  useEffect(() => {
    fetchDrivers();
  }, []);

  useEffect(() => {
    if (admin?.userId && accessToken) {
      fetchPackkyData(
        fromDate,
        toDate,
        setRestaurantData,
        setDriverTaskAnalytics,
        setTaskStatusAnalytics,
      );
    }
  }, [admin, accessToken]);

  const handleOpen = () => {
    if (!selectedDriver) return;

    const driverId = selectedDriver.driverId;

    const username = encodeURIComponent(
      selectedDriver.username || selectedDriver.fullName,
    );

    navigate(`/analytics/${driverId}/${username}`);

    setConfirmOpen(false);
    setSelectedDriver(null);
  };

  return (
    <>
      <div className="h-screen overflow-hidden flex flex-col p-6 animate-fade-in">
        <div className="relative z-[70] shrink-0 flex items-center justify-between mb-4">
          <div className="flex items-center">
            <img src={Logo} alt="Logo" className="h-10" />
            <p className="text-2xl font-bold ps-3">
              {activeTab === "driver"
                ? "Driver Report"
                : activeTab === "driverIntelligence"
                  ? "Driver Intelligence"
                  : activeTab === "points"
                    ? "Driver Performance Points"
                    : "Report"}
            </p>
          </div>
        </div>

        {loading ? (
          <LoadingScreen />
        ) : (
          <>
            <div className="relative z-[60] shrink-0 border-b border-gray-200">
              <nav className="-mb-px flex space-x-8 overflow-x-auto">
                <button
                  onClick={() => setActiveTab("packky")}
                  className={`shrink-0 py-3 px-1 border-b-2 text-sm font-medium ${
                    activeTab === "packky"
                      ? "border-orange-600 text-orange-600"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Packky Reports
                </button>

                <button
                  onClick={() => setActiveTab("driverIntelligence")}
                  className={`shrink-0 py-3 px-1 border-b-2 text-sm font-medium ${
                    activeTab === "driverIntelligence"
                      ? "border-orange-600 text-orange-600"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Driver Intelligence
                </button>

                <button
                  onClick={() => setActiveTab("points")}
                  className={`shrink-0 py-3 px-1 border-b-2 text-sm font-medium ${
                    activeTab === "points"
                      ? "border-orange-600 text-orange-600"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Driver Points
                </button>

                <button
                  onClick={() => setActiveTab("driver")}
                  className={`shrink-0 py-3 px-1 border-b-2 text-sm font-medium ${
                    activeTab === "driver"
                      ? "border-orange-600 text-orange-600"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Driver Reports
                </button>
              </nav>
            </div>

            {/* Only content below tabs scrolls */}
            <div className="relative z-0 flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain pt-4 pb-10">
              {activeTab === "driver" && (
                <div className="space-y-4">
                  {/* Existing driver search / refresh */}
                  <div className="flex flex-wrap md:flex-nowrap gap-3 items-end">
                    <input
                      type="text"
                      placeholder="Search by name, code, email, phone..."
                      value={search}
                      onChange={(e) => {
                        setSearch(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="px-3 py-2 border rounded text-sm w-full md:w-64"
                    />

                    <div className="flex gap-2 ml-auto">
                      <button
                        className="min-w-[96px] h-10 px-3 py-1 bg-green-500 text-white rounded"
                        onClick={() => {
                          fetchPackkyData(
                            fromDate,
                            toDate,
                            setRestaurantData,
                            setDriverTaskAnalytics,
                            setTaskStatusAnalytics,
                          );
                          fetchDrivers();
                        }}
                      >
                        Refresh
                      </button>
                    </div>
                  </div>

                  {/* Existing driver table always visible */}
                  <DriversReportsTab
                    loadingDrivers={loadingDrivers}
                    currentDrivers={currentDrivers}
                    onOpenAnalytics={(driver) => {
                      setSelectedDriver(driver);
                      setConfirmOpen(true);
                    }}
                  />

                  {renderPagination()}

                  {/* Weekly audit records directly under Driver Reports */}
                  <DriverIntelligenceTab
                    initialFromDate={fromDate}
                    initialToDate={toDate}
                    drivers={drivers}
                    viewMode="table"
                  />
                </div>
              )}

              {activeTab === "driverIntelligence" && (
                <DriverIntelligenceTab
                  initialFromDate={fromDate}
                  initialToDate={toDate}
                  drivers={drivers}
                  viewMode="chart"
                />
              )}

              {activeTab === "points" && <DriverPointsTab drivers={drivers} />}

              {activeTab === "packky" && (
                <PackkyReportsTab
                  fromDate={fromDate}
                  setFromDate={setFromDate}
                  toDate={toDate}
                  setToDate={setToDate}
                  restaurantData={restaurantData}
                  driverTaskAnalytics={driverTaskAnalytics}
                  taskStatusAnalytics={taskStatusAnalytics}
                  onApplyFilter={() =>
                    fetchPackkyData(
                      fromDate,
                      toDate,
                      setRestaurantData,
                      setDriverTaskAnalytics,
                      setTaskStatusAnalytics,
                    )
                  }
                  onExport={handlePackkyExport}
                />
              )}
            </div>
          </>
        )}
      </div>

      {confirmOpen && (
        <div className="fixed inset-0  z-[9999] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-lg w-[90%] max-w-sm p-5">
            <h3 className="text-lg font-semibold mb-2">Open Driver Report</h3>

            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to open analytics for{" "}
              <span className="font-medium">{selectedDriver?.fullName}</span>?
            </p>

            <div className="flex justify-end gap-3">
              {/* Cancel */}
              <button
                onClick={() => {
                  setConfirmOpen(false);
                  setSelectedDriver(null);
                }}
                className="px-4 py-2 text-sm rounded border hover:bg-gray-50"
              >
                Cancel
              </button>

              {/* Open */}
              <button
                onClick={handleOpen}
                className="px-4 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700"
              >
                Open
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ReportsDashboard;
